import React, { useState, useEffect } from "react";
import { Button, Modal } from "antd";
import { svg } from "../Assets/svg";
import axiosInstance from "../helpers/axios";
import { getFullAwsUrl } from "../helpers/utils";

export interface DesktopVersionInfo {
  latest_version: string;
  win_url?: string;
  mac_url?: string;
  linux_url?: string;
}

export const isVersionNewer = (latest: string, current: string): boolean => {
  if (!latest || !current) return false;
  const parse = (v: string) => v.split(".").map((n) => parseInt(n, 10) || 0);
  const l = parse(latest);
  const c = parse(current);
  for (let i = 0; i < Math.max(l.length, c.length); i++) {
    const lNum = l[i] || 0;
    const cNum = c[i] || 0;
    if (lNum > cNum) return true;
    if (lNum < cNum) return false;
  }
  return false;
};

interface Props {
  visible: boolean;
  onClose: () => void;
  onProceed?: () => void;
  requiredVersion?: string;
  currentVersion?: string;
}

export const DesktopUpdateModal: React.FC<Props> = ({
  visible,
  onClose,
  onProceed,
  requiredVersion,
  currentVersion,
}) => {
  const [downloadingPlatform, setDownloadingPlatform] = useState<string | null>(null);

  const targetLatestVersion =
    requiredVersion || process.env.REACT_APP_DESKTOP_APP_VERSION || "1.0.1";
  const userCurrentVersion = currentVersion || localStorage.getItem("sm_desktop_app_version") || "1.0.0";

  const userAgent = window.navigator.userAgent.toLowerCase();
  const isWindows = userAgent.includes("win");
  const isMac = userAgent.includes("mac");
  const isLinux = userAgent.includes("linux");
  const showAll = !isWindows && !isMac && !isLinux;

  const handleDownload = async (platform: "windows" | "mac" | "linux", fallbackFileKey: string) => {
    setDownloadingPlatform(platform);
    let downloadUrl = "";

    try {
      const res = await axiosInstance.get(
        `/file-upload/app-download-url?platform=${platform}&version=${targetLatestVersion}`
      );
      if (res?.data?.data?.url) {
        downloadUrl = res.data.data.url;
      }
    } catch (err) {
      console.warn("Could not fetch presigned URL, using direct AWS URL fallback:", err);
    }

    if (!downloadUrl) {
      downloadUrl = getFullAwsUrl(fallbackFileKey);
    }

    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setDownloadingPlatform(null);
  };

  return (
    <Modal
      open={visible}
      centered
      width={"45rem"}
      footer={null}
      onCancel={onClose}
      closeIcon={svg?.closeIcon}
      title={<div className="text-center fs-4 fw-bolder lh-m">New Desktop App Update Available</div>}
      className="custom-modal"
    >
      <div className="d-flex flex-column gap-2 align-items-center mb-4">
        <p className="fs-6 text-center">
          A new version <strong>v{targetLatestVersion}</strong> of the SM-CRM Mail Gateway Desktop App is available.
          Your current installed version is <strong>v{userCurrentVersion}</strong>.
          <br />
          Please download and install the latest update for optimal performance and compatibility.
        </p>
      </div>

      <div className="row gx-sm-4 gx-2 gy-3 justify-content-center">
        {(isWindows || showAll) && (
          <div className="col-12 col-sm-auto">
            <Button
              loading={downloadingPlatform === "windows"}
              onClick={() => handleDownload("windows", `mail-apps/SM-CRM Mail Gateway Setup ${targetLatestVersion}.exe`)}
              className="btn btn-lg btn-primary w-100 h-auto px-sm-4 text-uppercase"
            >
              Download For Windows (v{targetLatestVersion})
            </Button>
          </div>
        )}
        {(isMac || showAll) && (
          <div className="col-12 col-sm-auto">
            <Button
              loading={downloadingPlatform === "mac"}
              onClick={() => handleDownload("mac", `mail-apps/SM-CRM Mail Gateway-${targetLatestVersion}.dmg`)}
              className="btn btn-lg btn-primary w-100 h-auto px-sm-4 text-uppercase"
            >
              Download For Mac (v{targetLatestVersion})
            </Button>
          </div>
        )}
        {(isLinux || showAll) && (
          <div className="col-12 col-sm-auto">
            <Button
              loading={downloadingPlatform === "linux"}
              onClick={() => handleDownload("linux", `mail-apps/SM-CRM Mail Gateway-${targetLatestVersion}.AppImage`)}
              className="btn btn-lg btn-primary w-100 h-auto px-sm-4 text-uppercase"
            >
              Download For Linux (v{targetLatestVersion})
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
